import React, { useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { toast } from 'react-toastify';
import { EMAIL_TOKENS, tokensForEmailType, tokensToHandlebars, handlebarsToTokens, buildChipHtml } from '../../utils/emailTokens';
import './EmailTemplateEditor.css';

// Resize + compress an image File to at most maxDim px on the longest side,
// returning a base64 data URL (JPEG q0.85). The data URL is inserted directly
// into the editor — no backend upload — so the body contains <img src="data:...">.
// At broadcast / transactional send time the backend converts data URIs into
// MIME inline attachments (cid:N) — standard email convention, recipients see
// images embedded in the message itself, not linked from a public URL.
async function compressImageFile(file, maxDim = 1200, quality = 0.85) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const longest = Math.max(img.width, img.height);
                const scale = Math.min(1, maxDim / longest);
                const w = Math.round(img.width * scale);
                const h = Math.round(img.height * scale);
                const canvas = document.createElement('canvas');
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, w, h);
                resolve(canvas.toDataURL('image/jpeg', quality));
            };
            img.onerror = reject;
            img.src = e.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

/**
 * Shared rich-text editor for school-voice emails (broadcasts + Mode 3 templates).
 *
 * Replaces the inline TinyMCE setup that used to live in CommunicationsPage.js
 * and the raw <textarea> + manual variable-button list in EmailTemplateEditorModal.js.
 * Both surfaces now share one component and one token-chip system.
 *
 * Usage modes:
 *  - Uncontrolled with imperative ref (e.g. CommunicationsPage compose tab):
 *      <EmailTemplateEditor ref={editorRef} emailType="broadcast" />
 *      // later: editorRef.current.getContent() → chip-form HTML
 *      //        editorRef.current.setContent(handlebarsHtml) → converts to chips first
 *  - Seeded with initial content (e.g. Settings Edit modal, Review modal):
 *      <EmailTemplateEditor initialHtml={savedTemplate} onChange={setBodyHtml} emailType="classPayment" />
 *
 * Note: the editor stores chips (`<span data-token="{{var}}">[Label]</span>`) in
 * its body. Callers MUST run `tokensToHandlebars(html)` before sending to the
 * backend — otherwise the backend's allowed-token regex won't match the chip
 * spans and templates will fail validation. See `EmailTemplateEditorModal.js`
 * and `CommunicationsPage.js` for the canonical save/send wiring.
 */
const EmailTemplateEditor = forwardRef(({
    initialHtml = '',
    onChange,
    emailType,
    height = 380,
    placeholder,
}, ref) => {
    const editorRef = useRef(null);
    const tokens = emailType ? tokensForEmailType(emailType) : EMAIL_TOKENS;

    // tokens are read inside TinyMCE's setup() callback which captures the
    // original closure. Use a ref so the menu's fetch() always reads the latest
    // emailType-filtered list — emailType may change while the editor is mounted
    // (e.g. EmailReviewModal switching between Mode 3 types).
    const tokensRef = useRef(tokens);
    tokensRef.current = tokens;

    useImperativeHandle(ref, () => ({
        // Returns chip-form HTML (TinyMCE native). Caller does
        // `tokensToHandlebars(...)` before posting to the backend.
        getContent: () => editorRef.current?.getContent() ?? '',
        // Convenience: returns Handlebars-form HTML ready to send.
        getHandlebars: () => tokensToHandlebars(editorRef.current?.getContent() ?? ''),
        // Seeds the editor with a Handlebars template (auto-converts to chips).
        setContent: (handlebarsHtml) => {
            if (editorRef.current) {
                editorRef.current.setContent(handlebarsToTokens(handlebarsHtml ?? ''));
            }
        },
        clear: () => {
            if (editorRef.current) editorRef.current.setContent('');
        },
        focus: () => editorRef.current?.focus?.(),
    }), []);

    // The TinyMCE init config is memoized only on `height` — the setup callback
    // closes over `tokensRef` (which is mutable) so emailType changes don't
    // require a re-init.
    const editorInit = useMemo(() => ({
        height,
        menubar: false,
        statusbar: false,
        // `lists` for bullets/numbers, `link` for hyperlinks, `autolink` for
        // typed-URL detection. The block selector + remove-format button are
        // built into core, no plugin needed.
        plugins: 'lists link autolink',
        toolbar: 'undo redo | blocks | bold italic underline removeformat | bullist numlist | link uploadimage | insertfield',
        // Surface only the block formats admins are likely to use. Heading 2/3
        // for section titles, Paragraph for body text. Excludes h1 (reserved for
        // email subject equivalent) and quote/code blocks (irrelevant for email).
        block_formats: 'Paragraph=p; Heading 2=h2; Heading 3=h3',
        // CSS variables don't resolve inside TinyMCE's sandboxed iframe — chip
        // styles in buildChipHtml use literal hex for the same reason.
        content_style: 'body { font-family: Arial, sans-serif; font-size: 14px; color: #333; } img { max-width: 100%; height: auto; }',
        branding: false,
        promotion: false,
        placeholder,
        setup: (editor) => {
            // Single-click image insert: file picker → client-side compress to
            // ≤1200px JPEG q0.85 → insert as data URI directly. The body sent
            // to the backend contains <img src="data:image/jpeg;base64,..."> which
            // sendBroadcast / sendTransactional then converts to MIME inline
            // attachments (cid:N) at send time.
            editor.ui.registry.addButton('uploadimage', {
                icon: 'image',
                tooltip: 'Insert image',
                onAction: () => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/png,image/jpeg,image/webp,image/gif';
                    input.onchange = async () => {
                        const file = input.files?.[0];
                        if (!file) return;
                        if (file.size > 10 * 1024 * 1024) {
                            toast.error('Image too large; please choose one under 10MB.');
                            return;
                        }
                        try {
                            const dataUrl = await compressImageFile(file);
                            editor.insertContent(`<img src="${dataUrl}" alt="" style="max-width:100%;height:auto;" />`);
                        } catch (err) {
                            toast.error(err?.message || 'Could not insert image.');
                        }
                    };
                    input.click();
                },
            });

            // "+ Insert Field" menu — inserts a non-editable token chip at cursor.
            // fetch() runs every time the menu opens, so tokensRef.current is
            // always the current emailType-filtered list.
            editor.ui.registry.addMenuButton('insertfield', {
                text: '+ Insert Field',
                fetch: (callback) => {
                    const items = tokensRef.current.map(({ handlebarsVar, label }) => ({
                        type: 'menuitem',
                        text: label,
                        onAction: () => {
                            editor.insertContent(buildChipHtml(handlebarsVar, label));
                        },
                    }));
                    callback(items);
                },
            });
        },
    }), [height, placeholder]);

    // Seed initial content via the Editor's initialValue prop so the editor
    // mounts already populated. This avoids a race where TinyMCE fires an empty
    // onEditorChange between mount and onInit's setContent — that wipes the
    // parent's body state, then onInit re-seeds, leaving the parent and editor
    // out of sync. Set initialValue on first mount only — TinyMCE ignores
    // subsequent prop changes (intentional). When the seed needs to change at
    // runtime, key the parent component on the seed identity.
    const seededValue = useMemo(
        () => (initialHtml ? handlebarsToTokens(initialHtml) : ''),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return (
        <div className="email-template-editor">
            <Editor
                tinymceScriptSrc="/tinymce/tinymce.min.js"
                licenseKey="gpl"
                onInit={(_evt, editor) => { editorRef.current = editor; }}
                initialValue={seededValue}
                init={editorInit}
                onEditorChange={(content) => onChange?.(content)}
            />
        </div>
    );
});

EmailTemplateEditor.displayName = 'EmailTemplateEditor';

export default EmailTemplateEditor;
